import { useState, useEffect } from "react";
import { CheckCircle, TrendingUp, DollarSign, Zap, X, Minus, ChevronUp } from "lucide-react";
import { API } from "@/utils/constants";

const STORAGE_KEY = "ff_live_activity_state";
const FIRM_NAMES_CACHE_KEY = "ff_active_firm_names";

// Realistic names and locations
const FIRST_NAMES = [
  "Michael", "James", "David", "Chris", "Daniel", "Matt", "Ryan", "Kevin",
  "Jason", "Brian", "Eric", "Mark", "Steve", "Tom", "Alex", "Nick",
  "Sarah", "Emily", "Jessica", "Ashley", "Amanda", "Rachel", "Lauren", "Megan"
];

const LOCATIONS = [
  "Texas", "Florida", "California", "New York", "Illinois", "Pennsylvania",
  "Ohio", "Georgia", "North Carolina", "Michigan", "Arizona", "Colorado",
  "Washington", "Virginia", "Massachusetts", "Tennessee", "Indiana", "Missouri",
  "London, UK", "Toronto, CA", "Sydney, AU", "Dubai, UAE", "Singapore"
];

// Fallback firms — overridden by API on load
let FIRMS = [
  "Bulenox", "BluSky Trading", "Elite Trader Funding", "Top One Futures",
  "Blue Guardian", "AquaFutures", "TradeDay", "YRM Prop"
];

// Load active firm names from API (cached)
(async () => {
  try {
    const cached = sessionStorage.getItem(FIRM_NAMES_CACHE_KEY);
    if (cached) { FIRMS = JSON.parse(cached); return; }
    const r = await fetch(`${API}/firms/active/names`);
    if (r.ok) {
      const data = await r.json();
      const names = data.map(f => f.name).filter(Boolean);
      if (names.length > 0) {
        FIRMS = names;
        sessionStorage.setItem(FIRM_NAMES_CACHE_KEY, JSON.stringify(names));
      }
    }
  } catch {}
})();

const ACCOUNT_SIZES = ["25K", "50K", "100K", "150K", "200K", "250K"];
const PAYOUT_AMOUNTS = ["$1,200", "$1,850", "$2,400", "$3,100", "$4,500", "$5,800", "$7,200", "$9,500"];

const ACTIVITY_TYPES = [
  { type: "started", weight: 40, icon: Zap, color: "text-cyan-400", bg: "bg-cyan-500/20" },
  { type: "passed", weight: 25, icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/20" },
  { type: "payout", weight: 20, icon: DollarSign, color: "text-amber-400", bg: "bg-amber-500/20" },
  { type: "funded", weight: 15, icon: TrendingUp, color: "text-purple-400", bg: "bg-purple-500/20" },
];

const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

const getWeightedActivityType = () => {
  const totalWeight = ACTIVITY_TYPES.reduce((sum, a) => sum + a.weight, 0);
  let random = Math.random() * totalWeight;
  for (const activity of ACTIVITY_TYPES) {
    random -= activity.weight;
    if (random <= 0) return activity;
  }
  return ACTIVITY_TYPES[0];
};

const generateActivity = () => {
  const activityType = getWeightedActivityType();
  const name = getRandomItem(FIRST_NAMES);
  const location = getRandomItem(LOCATIONS);
  const firm = getRandomItem(FIRMS);
  const size = getRandomItem(ACCOUNT_SIZES);
  const payout = getRandomItem(PAYOUT_AMOUNTS);

  let message = "";
  switch (activityType.type) {
    case "started":
      message = `${name} from ${location} just started a $${size} evaluation with ${firm}`;
      break;
    case "passed":
      message = `${name} from ${location} passed their ${firm} challenge!`;
      break;
    case "payout":
      message = `${name} from ${location} received ${payout} payout from ${firm}`;
      break;
    case "funded":
      message = `${name} from ${location} is now a funded trader with ${firm}`;
      break;
    default:
      message = `${name} from ${location} started with ${firm}`;
  }

  return {
    id: Date.now(),
    message,
    type: activityType.type,
    icon: activityType.icon,
    color: activityType.color,
    bg: activityType.bg,
  };
};

export const LiveActivityFeed = () => {
  const [widgetState, setWidgetState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "open"; } catch { return "open"; }
  });
  const [activities, setActivities] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const persistState = (state) => {
    setWidgetState(state);
    try { localStorage.setItem(STORAGE_KEY, state); } catch {}
  };

  useEffect(() => {
    const initial = Array.from({ length: 5 }, () => generateActivity());
    setActivities(initial);
    const showTimer = setTimeout(() => setIsVisible(true), 3000);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    if (activities.length === 0 || widgetState !== "open") return;
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex((prev) => {
          const next = (prev + 1) % activities.length;
          if (next === 0) {
            setActivities(prev => [...prev.slice(1), generateActivity()]);
          }
          return next;
        });
        setIsVisible(true);
      }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, [activities.length, widgetState]);

  if (activities.length === 0) return null;
  const current = activities[currentIndex];
  if (!current) return null;
  const Icon = current.icon;

  // Minimized pill
  if (widgetState === "min") {
    return (
      <button
        onClick={() => persistState("open")}
        className="fixed bottom-24 sm:bottom-6 left-4 sm:left-6 z-40 inline-flex items-center gap-1.5 bg-slate-900/90 backdrop-blur-sm border border-white/10 rounded-full px-3 py-2 shadow-lg hover:border-white/20 transition-colors"
        aria-label="Expand live activity feed"
        data-testid="live-activity-min-pill"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-xs text-slate-400 font-medium">Live Activity</span>
        <ChevronUp size={12} className="text-slate-500" />
      </button>
    );
  }

  // Closed pill
  if (widgetState === "closed") {
    return (
      <button
        onClick={() => persistState("open")}
        className="fixed bottom-24 sm:bottom-6 left-4 sm:left-6 z-40 inline-flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1.5 shadow-lg hover:border-white/20 transition-colors"
        aria-label="Show live activity feed"
        data-testid="live-activity-closed-pill"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-[10px] text-slate-500 font-medium">Live</span>
      </button>
    );
  }

  // Open state
  return (
    <div
      className={`fixed bottom-24 sm:bottom-6 left-4 sm:left-6 z-40 transition-all duration-500 max-w-[calc(100vw-2rem)] sm:max-w-xs ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      data-testid="live-activity-feed"
      role="complementary"
      aria-label="Live trader activity feed"
    >
      <div className="bg-slate-900/95 backdrop-blur-sm border border-white/10 rounded-xl p-3 shadow-xl shadow-black/20">
        {/* Controls */}
        <div className="flex items-center justify-end gap-1 mb-1">
          <button
            onClick={() => persistState("min")}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            aria-label="Minimize live activity feed"
            data-testid="live-activity-min-btn"
          >
            <Minus size={12} className="text-slate-500" />
          </button>
          <button
            onClick={() => persistState("closed")}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            aria-label="Close live activity feed"
            data-testid="live-activity-close-btn"
          >
            <X size={12} className="text-slate-500" />
          </button>
        </div>
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${current.bg} flex-shrink-0`}>
            <Icon size={16} className={current.color} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white leading-snug">{current.message}</p>
            <p className="text-[10px] text-slate-500 mt-1">just now</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-white/5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] text-slate-500">Live activity from verified traders</span>
        </div>
      </div>
    </div>
  );
};

export default LiveActivityFeed;
